import { validateContactConfig } from './contactPolicy.mjs';

export const CONTACT_AUTO_NAV_STORAGE_KEY = 'face.contact.landing-auto-nav-attempted.v1';
export const CONTACT_AUTO_NAV_LOAD_TIMEOUT_MS = 1000;

/**
 * @typedef {{ active: true, url: string, qrAssetPath: string }} ActiveContact
 * @typedef {{ active: false, reason: string }} InactiveContact
 * @typedef {ActiveContact | InactiveContact} ContactResolution
 * @typedef {{ navigate: true, url: string } | { navigate: false, reason: string }} AutoNavDecision
 * @typedef {{ getItem: (key: string) => string | null, setItem: (key: string, value: string) => void }} SessionStorage
 * @typedef {(url: string) => void} Navigator
 * @typedef {{ storage?: SessionStorage | null, navigate?: Navigator }} AutoNavDeps
 * @typedef {{ navigated: true, url: string } | { navigated: false, reason: string }} AutoNavResult
 * @typedef {{
 *   addEventListener?: (type: string, listener: () => void, options?: object) => void,
 *   removeEventListener?: (type: string, listener: () => void) => void,
 *   setTimeout?: (callback: () => void, delay: number) => unknown,
 *   clearTimeout?: (timerId: unknown) => void
 * }} AutoNavWindow
 * @typedef {{ readyState?: string }} AutoNavDocument
 * @typedef {{
 *   storage?: SessionStorage | null,
 *   navigate?: Navigator,
 *   win?: AutoNavWindow,
 *   doc?: AutoNavDocument,
 *   timeoutMs?: number,
 *   setTimeoutFn?: (callback: () => void, delay: number) => unknown,
 *   clearTimeoutFn?: (timerId: unknown) => void
 * }} AutoNavScheduleDeps
 */

/**
 * @param {ContactResolution} contact
 * @param {boolean} attempted
 * @returns {AutoNavDecision}
 */
export function decideContactAutoNavigation(contact, attempted) {
  if (!contact.active) return { navigate: false, reason: 'contact-inactive' };
  if (attempted) return { navigate: false, reason: 'already-attempted' };
  return { navigate: true, url: contact.url };
}

/**
 * @param {string} url
 */
function navigateWithWindow(url) {
  window.location.assign(url);
}

/**
 * @param {unknown} config
 * @param {AutoNavDeps} [deps]
 * @returns {AutoNavResult}
 */
export function runContactAutoNavigation(config, deps = {}) {
  const contact = validateContactConfig(config);
  const initial = decideContactAutoNavigation(contact, false);
  if (!initial.navigate) return { navigated: false, reason: initial.reason };

  const storage = deps.storage;
  if (!storage) return { navigated: false, reason: 'storage-unavailable' };

  let recorded;
  try {
    recorded = storage.getItem(CONTACT_AUTO_NAV_STORAGE_KEY);
  } catch {
    return { navigated: false, reason: 'storage-unavailable' };
  }

  const decision = decideContactAutoNavigation(contact, recorded !== null);
  if (!decision.navigate) return { navigated: false, reason: decision.reason };

  try {
    storage.setItem(CONTACT_AUTO_NAV_STORAGE_KEY, '1');
  } catch {
    return { navigated: false, reason: 'storage-unavailable' };
  }

  const navigate = deps.navigate || navigateWithWindow;
  try {
    navigate(decision.url);
  } catch {
    return { navigated: false, reason: 'navigation-failed' };
  }

  return { navigated: true, url: decision.url };
}

/**
 * Registers at most one automatic navigation after the page load completes or
 * the bounded timeout elapses, whichever happens first. The returned cancel
 * function removes the pending listener and timer.
 * @param {unknown} config
 * @param {AutoNavScheduleDeps} [deps]
 * @returns {() => void}
 */
export function scheduleContactAutoNavigation(config, deps = {}) {
  const win = deps.win || window;
  const doc = deps.doc || document;
  const timeoutMs = deps.timeoutMs === undefined
    ? CONTACT_AUTO_NAV_LOAD_TIMEOUT_MS
    : deps.timeoutMs;
  const setTimer = deps.setTimeoutFn || ((callback, delay) => win.setTimeout(callback, delay));
  const clearTimer = deps.clearTimeoutFn || ((timerId) => win.clearTimeout(timerId));
  let finished = false;
  let timerId = null;

  const cleanup = () => {
    if (timerId !== null) {
      clearTimer(timerId);
      timerId = null;
    }
    if (win.removeEventListener) {
      win.removeEventListener('load', onLoad);
    }
  };

  function trigger() {
    if (finished) return;
    finished = true;
    cleanup();
    runContactAutoNavigation(config, {
      storage: deps.storage,
      navigate: deps.navigate
    });
  }

  function onLoad() {
    if (finished) return;
    cleanup();
    timerId = setTimer(trigger, 0);
  }

  if (doc.readyState === 'complete') {
    timerId = setTimer(trigger, 0);
  } else {
    if (win.addEventListener) {
      win.addEventListener('load', onLoad, { once: true });
    }
    timerId = setTimer(trigger, timeoutMs);
  }

  return () => {
    if (finished) return;
    finished = true;
    cleanup();
  };
}
